import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef } from 'react'

export const ROUTE_CHANGE_ABORTED_ERROR_NAME = 'RouteChangeAbortedError'

class RouteChangeAbortedError extends Error {
  constructor() {
    super('Route change aborted: the form has unsaved changes')
    this.name = ROUTE_CHANGE_ABORTED_ERROR_NAME
  }
}

export const isRouteChangeAbortedError = (error: unknown): boolean =>
  error instanceof RouteChangeAbortedError

interface UseUnsavedChangesWarningOptions {
  hasUnsavedChanges: boolean
  message: string
}

const useUnsavedChangesWarning = ({
  hasUnsavedChanges,
  message,
}: UseUnsavedChangesWarningOptions) => {
  const router = useRouter()
  const isNavigationAllowedRef = useRef(false)

  const allowNavigation = useCallback(() => {
    isNavigationAllowedRef.current = true
  }, [])

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return
    }

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isNavigationAllowedRef.current) {
        return
      }

      event.preventDefault()
      event.returnValue = ''
    }

    const beforePopState = () => {
      if (isNavigationAllowedRef.current || window.confirm(message)) {
        isNavigationAllowedRef.current = true
        return true
      }

      // Push the current URL back so the address bar stays in sync.
      window.history.pushState(null, '', router.asPath)
      return false
    }

    const onRouteChangeStart = () => {
      if (isNavigationAllowedRef.current || window.confirm(message)) {
        return
      }

      router.events.emit('routeChangeError')
      throw new RouteChangeAbortedError()
    }

    window.addEventListener('beforeunload', onBeforeUnload)
    router.events.on('routeChangeStart', onRouteChangeStart)
    router.beforePopState(beforePopState)

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      router.events.off('routeChangeStart', onRouteChangeStart)
      router.beforePopState(() => true)
    }
  }, [hasUnsavedChanges, message, router])

  return { allowNavigation }
}

export default useUnsavedChangesWarning
